import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/shared";
import { Plus, Pencil, Eye, Trash2, Globe, Archive } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft: "bg-kid-mist text-kid-muted", "in_review": "bg-kid-sun/20 text-[#8a6100]",
  scheduled: "bg-kid-sky/20 text-kid-tealcta", published: "bg-kid-green/15 text-[#3f9d5c]",
  archived: "bg-kid-coral/15 text-kid-coral",
};
const scoreColor = (s) => (s >= 80 ? "text-[#3f9d5c]" : s >= 60 ? "text-[#8a6100]" : "text-kid-coral");

export default function ArticleStudio() {
  const [articles, setArticles] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/admin/articles"), api.get("/article-categories")]);
    setArticles(a.data); setCats(c.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);
  if (loading) return <Spinner />;

  const catLabel = (k) => (cats.find((c) => c.id === k) || {}).name || "—";
  const filtered = statusFilter === "all" ? articles : articles.filter((a) => a.status === statusFilter);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const publish = (a) => act(() => api.post(`/admin/articles/${a.id}/publish`), "Article published.");
  const unpublish = (a) => act(() => api.post(`/admin/articles/${a.id}/unpublish`), "Moved to draft.");
  const archive = (a) => act(() => api.post(`/admin/articles/${a.id}/archive`), "Article archived.");
  const del = (a) => { if (window.confirm(`Delete "${a.title || "Untitled"}"?`)) act(() => api.delete(`/admin/articles/${a.id}`), "Article deleted."); };

  const STATUSES = ["all", "draft", "scheduled", "published", "archived"];

  return (
    <div data-testid="article-studio">
      <PageHeader eyebrow="Content" title="Article Studio" subtitle="Generate, edit, and publish SEO-friendly articles for Canadian families."
        action={<Link to="/ops/article-studio/new" className="kid-btn kid-btn-primary h-11 px-5" data-testid="article-new-btn"><Plus size={16} /> New article</Link>} />

      <div className="flex flex-wrap gap-2 mb-4" data-testid="article-status-filters">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`article-filter-${s}`}
            className={`text-sm font-semibold rounded-full px-4 py-1.5 border capitalize transition-colors ${statusFilter === s ? "bg-kid-tealcta text-white border-kid-tealcta" : "border-kid-line text-kid-navy hover:border-kid-teal"}`}>
            {s} {s !== "all" ? `(${articles.filter((a) => a.status === s).length})` : `(${articles.length})`}
          </button>
        ))}
      </div>

      <div className="kid-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="articles-table">
          <thead className="bg-kid-mist text-kid-muted text-left"><tr>
            <th className="px-5 py-3 font-poppins font-semibold">Title</th><th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Category</th><th className="px-4 py-3">Keyword</th>
            <th className="px-4 py-3">Score</th><th className="px-4 py-3">Updated</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-kid-muted">No articles here yet.</td></tr>}
            {filtered.map((a) => (
              <tr key={a.id} className="border-t border-kid-line align-middle" data-testid={`article-row-${a.id}`}>
                <td className="px-5 py-3 font-medium text-kid-navy max-w-xs"><div className="truncate">{a.title || "Untitled draft"}</div><div className="text-xs text-kid-muted truncate">/articles/{a.slug}</div></td>
                <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full capitalize ${STATUS_STYLES[a.status] || "bg-kid-mist"}`}>{a.status}</span></td>
                <td className="px-4 py-3">{catLabel(a.category_id)}</td>
                <td className="px-4 py-3 text-kid-muted truncate max-w-[140px]">{a.primary_keyword || "—"}</td>
                <td className={`px-4 py-3 font-bold ${scoreColor(a.article_readiness_score || 0)}`}>{a.article_readiness_score || 0}</td>
                <td className="px-4 py-3 text-kid-muted whitespace-nowrap">{a.updated_at ? new Date(a.updated_at).toLocaleDateString("en-CA") : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => navigate(`/ops/article-studio/edit/${a.id}`)} title="Edit" className="text-kid-teal p-1" data-testid={`article-edit-${a.id}`}><Pencil size={16} /></button>
                    <button onClick={() => navigate(`/ops/article-studio/preview/${a.id}`)} title="Preview" className="text-kid-muted hover:text-kid-navy p-1" data-testid={`article-preview-${a.id}`}><Eye size={16} /></button>
                    {a.status !== "published" ? <button onClick={() => publish(a)} title="Publish" className="text-[#3f9d5c] p-1" data-testid={`article-publish-${a.id}`}><Globe size={16} /></button>
                      : <button onClick={() => unpublish(a)} title="Unpublish" className="text-kid-muted hover:text-kid-navy p-1" data-testid={`article-unpublish-${a.id}`}><Globe size={16} /></button>}
                    {a.status !== "archived" && <button onClick={() => archive(a)} title="Archive" className="text-kid-muted hover:text-kid-coral p-1" data-testid={`article-archive-${a.id}`}><Archive size={16} /></button>}
                    <button onClick={() => del(a)} title="Delete" className="text-kid-muted hover:text-kid-coral p-1" data-testid={`article-delete-${a.id}`}><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
